import { identify, resetIdentity, setGroup } from "@utils/analytics.ts";
import { queryClient } from "@utils/query-client.ts";
import { AppStore } from "../jotai";
import { accessTokenAtom, refreshTokenClaimsAtom, tokenAtom } from "./auth-atoms";
import type { AccessTokenClaims, RefreshTokenClaims } from "./auth-schema";

const IMPERSONATOR = "impersonator";
const TOUR_PERSISTENCE_KEY_PREFIX = "definity:tours";

/** Clears tour-kit persistence keys on logout. */
function clearTourPersistence() {
  if (typeof window === "undefined") {
    return;
  }

  for (const key of Object.keys(window.localStorage)) {
    if (key.startsWith(`${TOUR_PERSISTENCE_KEY_PREFIX}:`)) {
      window.localStorage.removeItem(key);
    }
  }
}

/**
 * When access and refresh tokens disagree on `user_id`, the refresh token still belongs to the
 * support user; returns that id for analytics distinct_id. Otherwise `null`.
 */
function impersonationSupportUserId(
  refreshClaims: RefreshTokenClaims | null,
  accessClaims: AccessTokenClaims
): number | null {
  if (refreshClaims === null || refreshClaims.user_id === accessClaims.user_id) {
    return null;
  }
  return refreshClaims.user_id;
}

/**
 * Syncs the current auth state to PostHog. Called on every access token change (login, refresh, logout).
 *
 * Impersonation detection: when a support user impersonates a tenant user, the access token is
 * issued for the impersonated user while the refresh token still belongs to the real session owner.
 * A user_id mismatch between the two tokens signals an impersonation session — in that case we
 * identify as the impersonator (not the impersonated user) to keep support activity out of real
 * user analytics. Impersonation sessions are filtered in PostHog by the masked email "impersonator@definity.ai".
 */
export function identifyUser(
  accessClaims: AccessTokenClaims | null,
  refreshClaims: RefreshTokenClaims | null
) {
  if (!accessClaims) {
    resetIdentity();
    clearTourPersistence();
    return;
  }

  const supportId = impersonationSupportUserId(refreshClaims, accessClaims);
  const tenantId = accessClaims.tenant_id.toString();

  if (supportId !== null) {
    // supportId is the real support user's ID — during impersonation the access token is issued
    // for the impersonated user, so accessClaims.user_id would be the wrong identity.
    // Prefix is intentional: without it, distinct_id would equal the support user's real user_id,
    // merging impersonation-session events into their genuine PostHog profile.
    identify(`${IMPERSONATOR}_${supportId}`, {
      license: accessClaims.license,
      tenant_id: tenantId,
      email: `${IMPERSONATOR}@definity.ai`,
      impersonated_user_id: accessClaims.user_id.toString(),
    });
  } else {
    identify(accessClaims.user_id.toString(), {
      license: accessClaims.license,
      tenant_id: tenantId,
      email: accessClaims.email,
    });
  }

  // Per-tenant funnels, retention, feature flags — https://posthog.com/docs/product-analytics/group-analytics
  setGroup("tenant", tenantId, { license: accessClaims.license });
}

function getAuthClaims() {
  return {
    accessClaims: AppStore.get(tokenAtom),
    refreshClaims: AppStore.get(refreshTokenClaimsAtom),
  };
}

/** Call once at app startup to register auth-change side effects. */
export function initAuthEffects() {
  AppStore.sub(accessTokenAtom, () => {
    void queryClient.invalidateQueries();

    const { accessClaims, refreshClaims } = getAuthClaims();
    identifyUser(accessClaims, refreshClaims);
  });
}
